/**
 * AppJobDetailsModal Component
 *
 * Modal for viewing appJob details.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { useAppJobDetails } from '../hooks/useAppJobDetails';
import { formatRelativeTime, formatDateTime } from '../../../lib/utils/format';
import { describeCronExpression, formatPayload } from '../../../lib/validators/app-job';
import { getErrorMessage } from '../../../lib/utils/errors';
import { AppJobStatus } from '../../../types/app-job.types';

interface AppJobDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  appJobId: string;
}

/**
 * Get status badge styling
 */
function getStatusBadge(status: AppJobStatus): { className: string; label: string } {
  switch (status) {
    case AppJobStatus.RUNNING:
      return { className: 'bg-green-100 text-green-800', label: 'Running' };
    case AppJobStatus.PAUSED:
      return { className: 'bg-yellow-100 text-yellow-800', label: 'Paused' };
    case AppJobStatus.DISABLED:
      return { className: 'bg-gray-100 text-gray-800', label: 'Disabled' };
    default:
      return { className: 'bg-gray-100 text-gray-800', label: status };
  }
}

/**
 * AppJobDetailsModal component
 */
export const AppJobDetailsModal: React.FC<AppJobDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  appJobId,
}) => {
  const { data: appJob, isLoading, error } = useAppJobDetails(tenantId, appJobId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header>App Job Details</Modal.Header>
      <Modal.Body>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Content */}
        {appJob && !isLoading && (
          <div className="space-y-6">
            {/* Header with Name and Status */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{appJob.appJobName}</h3>
                {appJob.description && (
                  <p className="text-sm text-gray-500 mt-1">{appJob.description}</p>
                )}
              </div>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  getStatusBadge(appJob.status).className
                }`}
              >
                {getStatusBadge(appJob.status).label}
              </span>
            </div>

            {/* Application & Service Account */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-primary-50 p-4 rounded-lg">
                <div className="text-xs font-medium text-primary-600 uppercase">Application</div>
                <div className="mt-1 text-lg font-semibold text-primary-900">
                  {appJob.applicationCode}
                </div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-xs font-medium text-purple-600 uppercase">Service Account</div>
                <div className="mt-1 text-lg font-semibold text-purple-900">
                  {appJob.serviceAccountName || 'N/A'}
                </div>
              </div>
            </div>

            {/* Execution Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900">Execution Configuration</h4>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Function</span>
                  <code className="text-sm font-medium bg-white px-2 py-1 rounded border">
                    {appJob.functionName}
                  </code>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Schedule</span>
                  <span className="text-sm font-medium text-gray-900">
                    {describeCronExpression(appJob.cronExpression)}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Cron Expression</span>
                  <code className="text-sm font-medium bg-white px-2 py-1 rounded border">
                    {appJob.cronExpression}
                  </code>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Timezone</span>
                  <span className="text-sm font-medium text-gray-900">{appJob.timezone}</span>
                </div>
              </div>
            </div>

            {/* Payload */}
            {appJob.payload && Object.keys(appJob.payload).length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Payload</h4>
                <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded-lg overflow-x-auto">
                  {formatPayload(appJob.payload)}
                </pre>
              </div>
            )}

            {/* Execution Status */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-900">Execution Status</h4>

              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Next Run</span>
                  <span className="text-sm font-medium text-gray-900">
                    {appJob.nextRunAt
                      ? `${formatDateTime(appJob.nextRunAt)} (${formatRelativeTime(appJob.nextRunAt)})`
                      : 'Not appJobd'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Last Run</span>
                  <span className="text-sm font-medium text-gray-900">
                    {appJob.lastRunAt
                      ? `${formatDateTime(appJob.lastRunAt)} (${formatRelativeTime(appJob.lastRunAt)})`
                      : 'Never'}
                  </span>
                </div>

                {appJob.lastRunStatus && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Last Run Status</span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        appJob.lastRunStatus === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {appJob.lastRunStatus === 'completed' ? 'Success' : 'Failed'}
                    </span>
                  </div>
                )}

                {appJob.lastRunJobId && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Last Job ID</span>
                    <code className="text-xs bg-white px-2 py-1 rounded border">
                      {appJob.lastRunJobId}
                    </code>
                  </div>
                )}
              </div>
            </div>

            {/* Metadata */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Schedule ID</span>
                <code className="text-xs bg-gray-100 px-2 py-1 rounded">{appJob.appJobId}</code>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Created</span>
                <span className="text-gray-900">
                  {formatDateTime(appJob.createdAt)} ({formatRelativeTime(appJob.createdAt)})
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Last Updated</span>
                <span className="text-gray-900">
                  {formatDateTime(appJob.updatedAt)} ({formatRelativeTime(appJob.updatedAt)})
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end pt-4">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
